import React from "react";
import { useEffect, useState } from "react";
import { Alert, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { QueryValueInputProps } from "./QueryValueInput";
import { QueryFieldOption, QueryFieldTypes } from "system/query/QueryInfo";
import NullableAutocomplete from "components/uiElements/inputs/NullableAutocomplete";
import { Info } from "@mui/icons-material";

export function QueryFixedKeywordInput(props: QueryValueInputProps): JSX.Element {
  const { i18n } = useTranslation();

  const [keywordOptions, setKeywordOptions] = useState<QueryFieldOption[]>();
  const [kwOptsFieldId, setKwOptsFieldId] = useState<string | undefined>(undefined);

  useEffect(() => {
    let kwOpts = props.selectedField?.options?.slice(0) || [];
    if (props.selectedField?.type !== QueryFieldTypes.uid) {
      kwOpts.push({id: null, label: i18n.t("null")});
    }
    if (props.excludeOptions) {
      kwOpts = kwOpts.filter((option) => {
        return !props.excludeOptions?.includes(option.id as string);
      });
      if (props.value && props.excludeOptions?.includes(props.value as string)) {
        props.setValue(kwOpts[0].id, kwOpts[0].label);
        if (props.setLabel) {
          props.setLabel(kwOpts[0].label);
        }
      }
    }
    setKeywordOptions(kwOpts);
    setKwOptsFieldId(props.selectedField?.id);
  }, [i18n, props.excludeOptions, props.selectedField?.options]);

  useEffect(() => {
    // If the field id has changed, we need to wait for new options to be set
    if (kwOptsFieldId !== props.selectedField?.id) {
      return;
    }

    // If we have no set value and enforceSelection is true,
    // OR if the current value is in the excludeOptions list,
    // set the first option as the value
    const isNullAnOption = keywordOptions?.find((option) => option.id === null);
    if (props.enforceSelection
      && (props.value === null && !isNullAnOption)
      && (!props.value || props.excludeOptions?.includes(props.value as string))
      && keywordOptions && keywordOptions.length > 0) {
      const firstAllowedOption = keywordOptions.find((option) => {
        return option.id !== null && !props.excludeOptions?.includes(option.id as string);
      });
      if (firstAllowedOption) {
        props.setValue(firstAllowedOption.id, firstAllowedOption.label);
        if (props.setLabel) {
          props.setLabel(firstAllowedOption.label);
        }
      }
    }
  }, [kwOptsFieldId, props.enforceSelection, props.excludeOptions, keywordOptions, props.value, props.setValue]);

  return (
    <>
      {keywordOptions && keywordOptions?.length > 0 &&
        <NullableAutocomplete
          options={keywordOptions}
          isOptionEqualToValue={(option, value) =>  {
            return option.id === value.id;
          }}
          value={keywordOptions?.find(field => field.id === props.value) || keywordOptions[0]}
          onChange={(event: React.SyntheticEvent, newValue: QueryFieldOption | null) => {
            if (newValue) {
              props.setValue(newValue.id, newValue.label);
              if (props.setLabel) {
                props.setLabel(newValue.label);
              }
            }
          }}
          renderInput={(params) => <TextField autoComplete="off" {...params} />}
        />
      }
      {keywordOptions?.length === 0 && 
        <Alert color="info" icon={<Info />}>{i18n.t("query-form.no-options-available")}</Alert>
      }
    </>
  );
}
